// Style tab — the visual type's options (catalog `options`) as a format pane:
// collapsible sections, plain-language names with a one-line hint, the right
// control for each value (switch, segmented choice, colour swatches, palette
// strips, column pickers…), search, "Changed only" and a reset per setting.
//
// Which section an option belongs to, its name, hint, control and how its
// values read are DATA (styleOptions.json); an option the file does not know
// still shows — under "More settings", with the catalog's own heading. Every
// value is stored exactly as before (the catalog's own tokens: 'true'/'false',
// 'on'/'off', palette names, hex lists).
//
// The pane lays itself out from the width it is given (container queries in
// designerShellCss): one column when narrow, rows side by side when wide.

import { useMemo, useState, type ReactNode } from 'react'
import {
  ChevronDown, ChevronRight, Code2, Columns3, CreditCard, LayoutGrid, Map as MapIcon, MousePointerClick,
  Palette, RefreshCw, RotateCcw, Ruler, Search, Sigma, SlidersHorizontal, Table2, Tag, TrendingUp, Type, X,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { themePalette } from '@twasta/visual-engine'
import { useProjectStore } from '@/store/projectStore'
import type { VisualOption } from './types'
import DATA from './styleOptions.json'

interface OptionMeta { label?: string; hint?: string; unit?: string; placeholder?: string }
interface SectionDef { id: string; title: string; icon: string; options: string[] }
type ColourValue = string | string[]

const SECTIONS = DATA.sections as SectionDef[]
const META = DATA.options as Record<string, OptionMeta>
const CONTROLS = DATA.controls as Record<string, string>
const VALUE_LABELS = DATA.valueLabels as Record<string, Record<string, string>>
const COLOURS = DATA.colours as Record<string, ColourValue>
const OPEN_BY_DEFAULT = new Set(DATA.open as string[])
/** Options whose values are colour THEMES (visual-engine visualPalettes.json):
 *  their swatches show the theme's own colours — Default the page's. */
const THEME_PALETTE_OPTIONS = new Set((DATA.themePaletteOptions as string[] | undefined) ?? [])
function themeSwatch(name: string, appColor: string | null): ColourValue | null {
  try {
    // Default = the APPLICATION's colour (the project's theme_color), never
    // the builder's own accent the page root carries.
    const pal = themePalette(name, typeof document !== 'undefined' ? document.documentElement : null, appColor)
    return pal && pal.length ? (pal.length === 1 ? pal[0] : pal.slice(0, 4)) : null
  } catch {
    return null
  }
}

const SECTION_ICONS: Record<string, LucideIcon> = {
  tag: Tag, axes: Columns3, palette: Palette, sigma: Sigma, cards: CreditCard, map: MapIcon,
  table: Table2, trend: TrendingUp, pointer: MousePointerClick, size: Ruler, refresh: RefreshCw,
  code: Code2, more: SlidersHorizontal, grid: LayoutGrid,
}

/** An option's `showWhen` ("otherOption:val1,val2") against the live values. */
export function optionVisible(opt: VisualOption, byName: Map<string, string>): boolean {
  const rule = (opt as { showWhen?: string }).showWhen
  if (!rule) return true
  const idx = rule.indexOf(':')
  if (idx < 0) return true
  const dep = rule.slice(0, idx).trim()
  const wanted = rule.slice(idx + 1).split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)
  return wanted.includes((byName.get(dep) ?? '').toLowerCase())
}

const lovOf = (o: VisualOption) => (o.listOfValues || '').split(',').map((s) => s.trim()).filter(Boolean)
const TOGGLE_PAIRS: [string, string][] = [['true', 'false'], ['on', 'off'], ['yes', 'no']]
/** The stored on/off tokens of a two-state option, or null. */
function toggleTokens(o: VisualOption): { on: string; off: string } | null {
  const lov = lovOf(o)
  if (!lov.length) return o.dataType === 'boolean' ? { on: 'true', off: 'false' } : null
  if (lov.length !== 2) return null
  const low = lov.map((v) => v.toLowerCase())
  for (const [on, off] of TOGGLE_PAIRS) {
    if (low.includes(on) && low.includes(off)) return { on: lov[low.indexOf(on)], off: lov[low.indexOf(off)] }
  }
  return null
}

function valueLabel(name: string, v: string): string {
  const own = VALUE_LABELS[name]?.[v]
  if (own) return own
  if (!v) return 'Default'
  if (/^[a-z]/.test(v)) { const s = v.replace(/[-_]+/g, ' '); return s.charAt(0).toUpperCase() + s.slice(1) }
  return v.replace(/([a-z])([A-Z])/g, '$1 $2')
}

function colourOf(name: string): ColourValue | null {
  return COLOURS[name] ?? COLOURS[name.toLowerCase()] ?? null
}
function swatchBg(c: ColourValue | null, name: string): string {
  if (Array.isArray(c)) return `linear-gradient(90deg, ${c.join(', ')})`
  if (c) return c
  if (/^transparent$/i.test(name)) return 'repeating-conic-gradient(#d6dce6 0% 25%, #ffffff 0% 50%) 50% / 10px 10px'
  if (/^#|^rgb/i.test(name)) return name
  return 'conic-gradient(#1f77b4, #2ca02c, #ff7f0e, #e377c2, #9467bd, #1f77b4)'   // "Default"
}

type Kind = 'toggle' | 'segment' | 'select' | 'swatch' | 'palette' | 'hex' | 'hex-list' | 'color'
  | 'column' | 'columns' | 'number' | 'text' | 'code'

function kindOf(o: VisualOption): Kind {
  const own = CONTROLS[o.optionName]
  if (own) return own as Kind
  if (o.optionType === 'code') return 'code'
  if (o.optionType === 'column') return 'column'
  if (o.optionType === 'column-multi') return 'columns'
  if (toggleTokens(o)) return 'toggle'
  const lov = lovOf(o)
  if (lov.length) {
    const labels = lov.map((v) => valueLabel(o.optionName, v))
    return lov.length <= 4 && labels.join('').length <= 30 ? 'segment' : 'select'
  }
  if (o.dataType === 'number') return 'number'
  return 'text'
}
/** Controls that sit beside the name; the rest take the row's full width. */
const INLINE: Kind[] = ['toggle', 'segment', 'number', 'hex', 'color', 'swatch']

export interface StyleOptionsProps {
  visualName: string
  options: VisualOption[]
  /** optionName → the catalog default. */
  defaults: Map<string, string>
  columns: { name: string; label: string }[]
  readOnly?: boolean
  onChange: (optionId: string, value: string) => void
  /** Shown above the sections (e.g. the map boundary status). */
  notice?: ReactNode
  /** The visual's own heading — always the first section. */
  heading?: {
    title: string
    subtitle: string
    /** What shows when the title is blank (the menu entry / object name). */
    titleFallback: string
    onChange: (field: 'title' | 'subtitle', value: string) => void
  }
}

export default function StyleOptions(p: StyleOptionsProps) {
  const [q, setQ] = useState('')
  const [changedOnly, setChangedOnly] = useState(false)
  const [openState, setOpenState] = useState<Record<string, boolean>>({})

  const byName = useMemo(() => new Map(p.options.map((o) => [o.optionName, o.defaultValue ?? ''])), [p.options])
  const isChanged = (o: VisualOption) => (p.defaults.get(o.optionName) ?? '') !== (o.defaultValue ?? '')
  const shown = p.options.filter((o) => optionVisible(o, byName))
  const changed = shown.filter(isChanged)

  const term = q.trim().toLowerCase()
  const matches = (o: VisualOption, sectionTitle: string) => {
    if (changedOnly && !isChanged(o)) return false
    if (!term) return true
    const m = META[o.optionName] || {}
    return [m.label, m.hint, o.optionHeading, o.optionName, sectionTitle]
      .some((s) => (s || '').toLowerCase().includes(term))
  }

  const sections = useMemo(() => {
    const placed = new Set<string>()
    const out: { def: SectionDef; opts: VisualOption[] }[] = []
    for (const def of SECTIONS) {
      const opts = def.options.map((n) => shown.find((o) => o.optionName === n)).filter((o): o is VisualOption => !!o)
      opts.forEach((o) => placed.add(o.optionName))
      if (opts.length) out.push({ def, opts })
    }
    const rest = shown.filter((o) => !placed.has(o.optionName))
    if (rest.length) out.push({ def: { id: 'more', title: 'More settings', icon: 'more', options: [] }, opts: rest })
    return out
  }, [shown])

  const filtered = sections
    .map((s) => ({ ...s, opts: s.opts.filter((o) => matches(o, s.def.title)) }))
    .filter((s) => s.opts.length)
  const isOpen = (id: string, opts: VisualOption[]) => {
    if (term || changedOnly) return true
    if (id in openState) return openState[id]
    return OPEN_BY_DEFAULT.has(id) || opts.some(isChanged) || sections.length <= 3
  }
  const resetAll = () => { for (const o of changed) p.onChange(o.optionId, p.defaults.get(o.optionName) ?? '') }
  const headingSet = p.heading ? [p.heading.title, p.heading.subtitle].filter((x) => x.trim()).length : 0
  const showHeading = !!p.heading
    && (!changedOnly || headingSet > 0)
    && (!term || ['title', 'subtitle', 'heading', 'name'].some((w) => w.includes(term) || term.includes(w)))
  const headingOpen = term || changedOnly ? true : openState.heading ?? true

  return (
    <div className="vso">
      <div className="vso-head">
        <div className="vso-title">
          Style <span className="s">· {p.visualName}</span>
          <span style={{ flex: 1 }} />
          {changed.length > 0 && !p.readOnly && (
            <button type="button" className="vso-link" onClick={resetAll} title="Put every setting back to its default">
              Reset all
            </button>
          )}
        </div>
        <div className="vso-tools">
          <label className="vdo-search vso-search">
            <Search size={15} />
            <input type="search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Find a setting (${shown.length})`} aria-label="Find a setting" />
            {q && <button type="button" className="vdo-ib sm" aria-label="Clear" onClick={() => setQ('')}><X size={13} /></button>}
          </label>
          <button type="button" className={`vso-chip${changedOnly ? ' on' : ''}`} aria-pressed={changedOnly}
            onClick={() => setChangedOnly((v) => !v)} disabled={!changed.length && !changedOnly}
            title="Show only the settings you have changed">
            Changed <span className="n">{changed.length}</span>
          </button>
        </div>
      </div>

      {p.notice}

      {p.heading && showHeading && (
        <section className="vso-sec">
          <button type="button" className="vso-sec-h" aria-expanded={headingOpen}
            onClick={() => setOpenState((st) => ({ ...st, heading: !headingOpen }))}>
            <span className="ic"><Type size={15} /></span>
            <span className="t">Title</span>
            {headingSet > 0 && <span className="vso-dot" title={`${headingSet} set`}>{headingSet}</span>}
            <span style={{ flex: 1 }} />
            {headingOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
          {headingOpen && (
            <div className="vso-rows">
              {(['title', 'subtitle'] as const).map((f) => {
                const h = p.heading!
                const val = f === 'title' ? h.title : h.subtitle
                return (
                  <div key={f} className={`vso-row wide${val ? ' changed' : ''}`}>
                    <div className="vso-lab">
                      <span className="nm">{f === 'title' ? 'Title' : 'Subtitle'}</span>
                      <span className="hint">{f === 'title'
                        ? `Blank shows “${h.titleFallback}”.`
                        : 'A line under the title. Blank shows the menu entry’s description.'}</span>
                    </div>
                    <div className="vso-ctl">
                      <input type="text" className="vso-input" value={val} disabled={p.readOnly}
                        aria-label={f === 'title' ? 'Title' : 'Subtitle'} maxLength={f === 'title' ? 120 : 240}
                        placeholder={f === 'title' ? h.titleFallback : 'e.g. Last 30 days, all regions'}
                        onChange={(e) => h.onChange(f, e.target.value)} />
                      {val && !p.readOnly && (
                        <button type="button" className="vso-reset" onClick={() => h.onChange(f, '')}
                          aria-label={`Clear the ${f}`} title="Clear"><RotateCcw size={13} /></button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </section>
      )}

      {!shown.length && !p.heading && <p className="vso-empty">This visual has no style settings.</p>}
      {shown.length > 0 && !filtered.length && !showHeading && (
        <p className="vso-empty">{changedOnly && !term ? 'Nothing changed yet — every setting is at its default.' : `No setting matches “${q}”.`}</p>
      )}

      {filtered.map(({ def, opts }) => {
        const Icon = SECTION_ICONS[def.icon] || SlidersHorizontal
        const open = isOpen(def.id, opts)
        const nChanged = opts.filter(isChanged).length
        return (
          <section key={def.id} className="vso-sec">
            <button type="button" className="vso-sec-h" aria-expanded={open}
              onClick={() => setOpenState((s) => ({ ...s, [def.id]: !open }))}>
              <span className="ic"><Icon size={15} /></span>
              <span className="t">{def.title}</span>
              {nChanged > 0 && <span className="vso-dot" title={`${nChanged} changed`}>{nChanged}</span>}
              <span style={{ flex: 1 }} />
              {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
            </button>
            {open && (
              <div className="vso-rows">
                {opts.map((o) => (
                  <OptionRow key={o.optionId} opt={o} changed={isChanged(o)} defaults={p.defaults}
                    columns={p.columns} readOnly={!!p.readOnly} onChange={p.onChange} />
                ))}
              </div>
            )}
          </section>
        )
      })}
    </div>
  )
}

function OptionRow({ opt, changed, defaults, columns, readOnly, onChange }: {
  opt: VisualOption; changed: boolean; defaults: Map<string, string>
  columns: { name: string; label: string }[]; readOnly: boolean
  onChange: (id: string, v: string) => void
}) {
  const meta = META[opt.optionName] || {}
  const kind = kindOf(opt)
  const label = meta.label || opt.optionHeading || opt.optionName
  const def = defaults.get(opt.optionName) ?? ''
  const set = (v: string) => { if (!readOnly) onChange(opt.optionId, v) }
  const wide = !INLINE.includes(kind) || (kind === 'swatch' && lovOf(opt).length > 4)
  return (
    <div className={`vso-row${wide ? ' wide' : ''}${changed ? ' changed' : ''}${kind === 'code' ? ' full' : ''}`}
      title={`${opt.optionHeading || opt.optionName} (${opt.optionName}) · default: ${def ? valueLabel(opt.optionName, def) : 'none'}`}>
      <div className="vso-lab">
        <span className="nm">{label}</span>
        {meta.hint && <span className="hint">{meta.hint}</span>}
      </div>
      <div className="vso-ctl">
        <Control opt={opt} kind={kind} meta={meta} columns={columns} readOnly={readOnly} set={set} label={label} />
        {changed && !readOnly && (
          <button type="button" className="vso-reset" onClick={() => onChange(opt.optionId, def)}
            aria-label={`Reset ${label} to its default`} title={`Back to default (${def ? valueLabel(opt.optionName, def) : 'none'})`}>
            <RotateCcw size={13} />
          </button>
        )}
      </div>
    </div>
  )
}

function Control({ opt, kind, meta, columns, readOnly, set, label }: {
  opt: VisualOption; kind: Kind; meta: OptionMeta; columns: { name: string; label: string }[]
  readOnly: boolean; set: (v: string) => void; label: string
}) {
  const v = opt.defaultValue ?? ''
  const lov = lovOf(opt)
  const appColor = useProjectStore((st) => st.currentProject?.application?.theme_color) || null
  switch (kind) {
    case 'toggle': {
      const t = toggleTokens(opt) || { on: 'true', off: 'false' }
      const on = v.toLowerCase() === t.on.toLowerCase()
      return (
        <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={readOnly}
          className={`vso-switch${on ? ' on' : ''}`} onClick={() => set(on ? t.off : t.on)}>
          <span />
        </button>
      )
    }
    case 'segment':
      return (
        <div className="vso-seg" role="radiogroup" aria-label={label}>
          {lov.map((x) => (
            <button key={x} type="button" role="radio" aria-checked={x.toLowerCase() === v.toLowerCase()}
              disabled={readOnly} className={x.toLowerCase() === v.toLowerCase() ? 'on' : ''} onClick={() => set(x)}>
              {valueLabel(opt.optionName, x)}
            </button>
          ))}
        </div>
      )
    case 'select':
      return (
        <select className="vso-input" value={v} disabled={readOnly} aria-label={label} onChange={(e) => set(e.target.value)}>
          {!lov.some((x) => x === v) && <option value={v}>{v ? valueLabel(opt.optionName, v) : 'Default'}</option>}
          {lov.map((x) => <option key={x} value={x}>{valueLabel(opt.optionName, x)}</option>)}
        </select>
      )
    case 'swatch': {
      // A blank stored value is the catalog's "Default" where it offers one.
      const current = v || lov.find((x) => /^default$/i.test(x)) || ''
      return (
        <div className="vso-swatches" role="radiogroup" aria-label={label}>
          {lov.map((x) => {
            const on = x.toLowerCase() === current.toLowerCase()
            return (
              <button key={x} type="button" role="radio" aria-checked={on} disabled={readOnly}
                className={`vso-sw${on ? ' on' : ''}`} title={valueLabel(opt.optionName, x)}
                aria-label={valueLabel(opt.optionName, x)} onClick={() => set(/^default$/i.test(x) ? '' : x)}>
                <span style={{
                  background: swatchBg(THEME_PALETTE_OPTIONS.has(opt.optionName) ? themeSwatch(x, appColor) : colourOf(x), x),
                }} />
              </button>
            )
          })}
        </div>
      )
    }
    case 'palette':
      return (
        <div className="vso-palettes" role="radiogroup" aria-label={label}>
          {!lov.includes(v) && v && <PaletteButton name={v} on set={set} readOnly={readOnly} />}
          {lov.map((x) => <PaletteButton key={x} name={x} on={x === v} set={set} readOnly={readOnly} />)}
        </div>
      )
    case 'hex':
      return <ColourInput value={v} onChange={set} readOnly={readOnly} label={label} />
    case 'color':
      return <ColourInput value={v} onChange={set} readOnly={readOnly} label={label} allowNames />
    case 'hex-list': {
      const parts = v.split(',').map((s) => s.trim())
      const names = ['Achieved', 'Track']
      return (
        <div className="vso-hexlist">
          {(parts.length ? parts : ['']).map((c, i) => (
            <span key={i} className="vso-hexpart">
              <ColourInput value={c} readOnly={readOnly} label={`${label} — ${names[i] || `colour ${i + 1}`}`}
                onChange={(nv) => { const next = [...parts]; next[i] = nv; set(next.join(',')) }} />
              <span className="cap">{names[i] || `Colour ${i + 1}`}</span>
            </span>
          ))}
        </div>
      )
    }
    case 'column':
      return (
        <select className="vso-input" value={v} disabled={readOnly} aria-label={label} onChange={(e) => set(e.target.value)}>
          <option value="">Automatic</option>
          {v && !columns.some((c) => c.name === v) && <option value={v}>{v}</option>}
          {columns.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
        </select>
      )
    case 'columns': {
      const sel = new Set(v.split(',').map((s) => s.trim()).filter(Boolean))
      if (!columns.length) return <span className="vso-faint">Add fields first.</span>
      return (
        <div className="vso-picks">
          {columns.map((c) => {
            const on = sel.has(c.name)
            return (
              <button key={c.name} type="button" aria-pressed={on} disabled={readOnly}
                className={`vso-pick${on ? ' on' : ''}`}
                onClick={() => {
                  const next = new Set(sel)
                  if (on) next.delete(c.name); else next.add(c.name)
                  set(columns.filter((x) => next.has(x.name)).map((x) => x.name).join(','))
                }}>{c.label}</button>
            )
          })}
        </div>
      )
    }
    case 'number':
      return (
        <span className="vso-num">
          <input type="number" className="vso-input" value={v} disabled={readOnly} aria-label={label}
            onChange={(e) => set(e.target.value)} />
          {meta.unit && <span className="u">{meta.unit}</span>}
        </span>
      )
    case 'code':
      return (
        <textarea className="vso-input vso-code" value={v} disabled={readOnly} aria-label={label} spellCheck={false} rows={12}
          onChange={(e) => set(e.target.value)}
          placeholder={'<?xml version="1.0"?>\n<xsl:stylesheet version="1.0" xmlns:xsl="http://www.w3.org/1999/XSL/Transform">\n  <xsl:template match="/rows"> … </xsl:template>\n</xsl:stylesheet>'} />
      )
    default:
      return (
        <input type="text" className="vso-input" value={v} disabled={readOnly} aria-label={label}
          placeholder={meta.placeholder} onChange={(e) => set(e.target.value)} />
      )
  }
}

function PaletteButton({ name, on, set, readOnly }: { name: string; on: boolean; set: (v: string) => void; readOnly: boolean }) {
  const parts = name.split('-').map((n) => colourOf(n)).map((c, i) => (Array.isArray(c) ? c[1] : c) || name.split('-')[i])
  return (
    <button type="button" role="radio" aria-checked={on} disabled={readOnly} className={`vso-pal${on ? ' on' : ''}`}
      title={name.replace(/-/g, ' · ')} aria-label={name.replace(/-/g, ', ')} onClick={() => set(name)}>
      {parts.map((c, i) => <span key={i} style={{ background: c }} />)}
    </button>
  )
}

/** A colour: a picker plus the value as text (a hex, or — `allowNames` — a
 *  colour name the chart understands, such as "black"). */
function ColourInput({ value, onChange, readOnly, label, allowNames }: {
  value: string; onChange: (v: string) => void; readOnly: boolean; label: string; allowNames?: boolean
}) {
  const hex = /^#[0-9a-f]{6}$/i.test(value) ? value
    : /^#[0-9a-f]{3}$/i.test(value) ? `#${value.slice(1).split('').map((c) => c + c).join('')}`
      : '#000000'
  return (
    <span className="vso-colour">
      <span className="vso-colour-sw" style={{ background: value || 'transparent' }}>
        <input type="color" value={hex} disabled={readOnly} aria-label={`${label} — pick`} onChange={(e) => onChange(e.target.value)} />
      </span>
      <input type="text" className="vso-input" value={value} disabled={readOnly} aria-label={label}
        placeholder={allowNames ? 'black or #1c2430' : '#ffffff'} onChange={(e) => onChange(e.target.value.trim())} />
    </span>
  )
}
